import { createContext, ReactNode, useContext, useEffect, useState } from 'react'

import { Avatar } from '@nextui-org/avatar'
import Link from 'next/link'
import Image from 'next/image'
import { Card, Spinner } from '@nextui-org/react'
import { toast } from 'react-toastify'

import { useCalculateTimeSincePost } from '@/hooks'
import { feedTypeCard, OriginalPost, PutFeedLikeParams } from '@/redux/api/feed/feed.type'
import ICONS from '@/assets/icons'
import { RenderMetaFromLink } from '@/app/(global)/post-cre/components/RenderFormFeed/RenderMetaFromLink'
import { MetadataLink } from '@/utils/fetchMetadata'
import { usePutFeedLikeMutation } from '@/redux/api/feed/feed.w3api'
import { RenderImagesPost } from '@/app/(global)/post-cre/components/RenderFormFeed/RenderImagesPost'
import LightboxImage from '@/components/shared/LightboxImage'
import { cn } from '@/utils/cn'
import { formatNumberUser } from '@/utils/formatNumberUser'
import { useGetMetaData } from '@/hooks/useGetMetaData'

import BtnTool from './BtnTool'
import styles from './styles.module.css'
import { ContentParsed } from '../../ContentParsed'

const FeedItemContext = createContext<feedTypeCard | null>(null)

const ItemFeed = ({ item, children }: { item: feedTypeCard | undefined; children?: React.ReactNode }) => {
  if (!item) return null
  return <FeedItemContext.Provider value={item}>{children}</FeedItemContext.Provider>
}

const ItemFeedContent = ({
  item,
  children,
  onComment,
  onUpdatePost,
  onDelete,
}: {
  item?: feedTypeCard
  children: ReactNode
  onComment?: any
  onUpdatePost?: () => void
  onDelete?: () => void
}) => {
  const _item = useContext(FeedItemContext)
  const data = item ?? _item
  if (!data) return null
  return (
    <div className="border-b border-white/10 py-3">
      <div className={`${styles.cards__ss__1}`}>
        <div>
          <Link href={data?.postAs === 'SPACE' ? `/spaces/${data.creatorId}` : `/profile/${data.creatorId}`}>
            <Avatar className="h-[40px] w-[40px]" src={`${data?.avatarImage}`} alt={`${data?.avatarImage}`} />
          </Link>
        </div>
        <div className={styles.card__content__ss}>
          <div className={styles.headname__ss}>
            <div className={styles.info__ss}>
              <div className="flex gap-1.5 items-center flex-wrap md:flex-nowrap">
                <FeedName />
                <div className="flex items-center gap-1">
                  {/* <div className={styles.priceETH}>
                    <Image src={ICONS.key} alt="Key" width={20} height={20} />
                    <p>
                      <span className="text-sm font-medium text-ss-success-200">{Number(data?.priceBuyKey)}</span>
                      <span className="ml-0.5 text-[10px] text-ss-grayscale-300">USDT</span>
                    </p>
                  </div> */}
                  <div className={styles.time__card__ss}>
                    <div className="h-1 w-1 rounded-full bg-ss-grayscale-500" />
                    {/* eslint-disable-next-line react-hooks/rules-of-hooks */}
                    <p>{data.dateCreate && useCalculateTimeSincePost(data.dateCreate)}</p>
                  </div>
                </div>
              </div>

              <BtnTool
                onUpdate={onUpdatePost}
                onDelete={onDelete}
                postId={data.postId}
                creatorId={data.creatorId}
                isFollowed={data.isFollowing}
                postAs={data.postAs}
              />
            </div>
          </div>
          <FeedContent />

          {Number(data.imageUrls?.length) > 0 ? (
            <FeedImages onComment={onComment} images={data.imageUrls} />
          ) : (
            <FeedMetadata />
          )}
          {children}
        </div>
      </div>
    </div>
  )
}

const FeedName = ({
  info,
}: {
  info?: { postAs: 'SPACE' | 'USER'; nickname: string; creator: string; isKOL?: boolean }
}) => {
  const item = useContext(FeedItemContext)
  const data = {
    postAs: info?.postAs || item?.postAs,
    nickname: info?.nickname || item?.nickname,
    creator: info?.creator || item?.creatorId,
    isKOL: info?.hasOwnProperty('isKOL') ? info?.isKOL : item?.role?.value === 'kol',
  }

  return (
    <div
      className={cn({
        'flex items-center gap-x-1': data.isKOL,
      })}
    >
      <Link
        className={cn('font-inter text-sm font-medium truncate')}
        href={data.postAs === 'SPACE' ? `/spaces/${data.creator}` : `/profile/${data.creator}`}
      >
        {`${data.nickname}`}
      </Link>
      {data.isKOL && (
        <div>
          {data.postAs === 'SPACE' ? (
            <Image
              src={ICONS.icVerifySpace}
              alt=""
              className="w-5 h-5 md:w-[22px] md:h-[22px]"
              width={22}
              height={22}
            />
          ) : (
            <Image src={ICONS.verifyBadge} width={20} height={20} alt="Verify Badge" />
          )}
        </div>
      )}
    </div>
  )
}

const FeedContent = ({ content }: { content?: string; postId?: string }) => {
  const item = useContext(FeedItemContext)
  const data = content || item?.content
  if (!data) return null

  return <ContentParsed content={data} />
}

const FeedImages = ({ images }: { images?: any[]; postId?: string; onComment?: any }) => {
  const item = useContext(FeedItemContext)
  const [isOpen, setIsOpen] = useState(false)
  const data = images || item?.imageUrls || []
  if (!data.length) return null
  return (
    <>
      <div className={styles.img__card__ss} onClick={() => setIsOpen(true)} style={{ position: 'relative' }}>
        <RenderImagesPost images={data} />
      </div>
      <LightboxImage images={data} onClose={() => setIsOpen(false)} isOpen={isOpen} />
    </>
  )
}

const FeedMetadata = ({ content }: { content?: string }) => {
  const [metadata, setMetadata] = useState<MetadataLink | null>(null)
  const item = useContext(FeedItemContext)
  const data = content || item?.content
  const { handleGetMeta, loading } = useGetMetaData()

  useEffect(() => {
    const getLink = async () => {
      const meta = await handleGetMeta(data || '')
      setMetadata(meta)
    }
    getLink()
  }, [data])

  if (loading) return <Spinner size="sm" />

  if (!metadata) return null

  return <RenderMetaFromLink metadata={metadata} />
}

const FeedShare = ({ feedShare, onComment }: { feedShare?: OriginalPost; onComment?: any }) => {
  const item = useContext(FeedItemContext)
  const data = feedShare || item?.originalPost

  if (!data) return null

  return (
    <Card className="border border-white/10 bg-ss-dark">
      <div className={`${styles.cards__ss__1} p-4`}>
        <div>
          <Link href={data?.postAs === 'SPACE' ? `/spaces/${data.creatorId}` : `/profile/${data.creatorId}`}>
            <Avatar className="h-[40px] w-[40px]" src={`${data?.avatarImage}`} alt={`${data?.avatarImage}`} />
          </Link>
        </div>
        <div className={styles.card__content__ss}>
          <div className={styles.headname__ss}>
            <div className={styles.info__ss}>
              <div className="flex flex-col gap-1.5 md:flex-row md:items-center">
                <FeedName
                  info={{
                    creator: data.creatorId,
                    nickname: data.nickname,
                    isKOL: data.role?.value === 'kol',
                    postAs: data.postAs,
                  }}
                />
              </div>
            </div>
          </div>
          <FeedContent content={data.content} />
          {data.imageUrls?.length > 0 && <FeedImages images={data.imageUrls} onComment={onComment} />}
        </div>
      </div>
    </Card>
  )
}

const FeedAction = ({
  item,
  onComment,
  onSharePost,
}: {
  item?: feedTypeCard
  index: number
  onComment: () => void
  onSharePost?: () => void
}) => {
  const _item = useContext(FeedItemContext)
  const data = item ?? _item

  // const [putFeed] = usePutFeedMutation()
  const [putFeedLike] = usePutFeedLikeMutation()
  // const [canVote, setCanVote] = useState(true)
  // const [votes, setVotes] = useState<Record<number, string>>({})
  // const [totals, setTotals] = useState<Record<number, number>>({})
  const [isLiked, setIsLiked] = useState<boolean>(false)
  const [totalLiked, setTotalLiked] = useState<number>(0)

  useEffect(() => {
    if (data) {
      setTotalLiked(data?.likeCount || 0)
      setIsLiked(data?.isLike || false)
    }
  }, [data])

  // const handleUpvote = (i: any, id: string, voteCount: number, voteType: string) => {
  //   if (!canVote) return
  //   const newVote = votes[i] === 'upvote' ? 'unvote' : 'upvote'
  //   setVotes({ ...votes, [i]: newVote })
  //   handleChangePutFeed(newVote, id, i, voteCount, voteType)
  // }

  // const handleDownvote = (i: any, id: string, voteCount: number, voteType: string) => {
  //   if (!canVote) return
  //   const newVote = votes[i] === 'downvote' ? 'unvote' : 'downvote'
  //   setVotes({ ...votes, [i]: newVote })
  //   handleChangePutFeed(newVote, id, i, voteCount, voteType)
  // }

  // const handleChangePutFeed = async (value: string, id: string, i: number, voteCount: number, voteType: string) => {
  //   setCanVote(false)
  //   let total = 0
  //   if (value === 'upvote') {
  //     total = voteType === 'downvote' ? 2 : 1
  //   }
  //   if (value === 'downvote') {
  //     total = voteType === 'upvote' ? -2 : -1
  //   }
  //   if (value === 'unvote') {
  //     total = votes[i] === 'upvote' ? (voteType === 'downvote' ? 1 : 0) : voteType === 'upvote' ? -1 : 0
  //   }
  //   setTotals({ ...totals, [i]: total })
  //   setVotes({ ...votes, [i]: value })
  //   const params: PutFeedParams = {
  //     postId: id,
  //     voteType: value,
  //   }
  //   try {
  //     setCanVote(false)
  //     const res = await putFeed(params).unwrap()
  //     if (res?.statusCode === 500) {
  //       setCanVote(true)
  //       toast.error(res.message)
  //       setTotals({ ...totals, [i]: 0 })
  //     } else {
  //       setCanVote(true)
  //     }
  //     // refetchData();
  //   } catch (err) {
  //     toast.error('Error vote post')
  //     setTotals({ ...totals, [i]: 0 })
  //     setCanVote(true)
  //   }
  // }

  const handleChangePutFeedLike = async (id: string) => {
    setIsLiked((prev) => !prev)

    if (!isLiked) {
      setTotalLiked((prev) => prev + 1)
    } else {
      setTotalLiked((prev) => (prev ? prev - 1 : 0))
    }

    const params: PutFeedLikeParams = {
      postId: id,
      isLike: !isLiked,
    }
    try {
      const res = await putFeedLike(params).unwrap()
      if (res?.statusCode === 500) {
        toast.error(res.message)
        // Revert the like state
        setIsLiked((prev) => !prev)
        if (isLiked) {
          setTotalLiked((prev) => prev + 1)
        } else {
          setTotalLiked((prev) => (prev ? prev - 1 : 0))
        }
      }
    } catch (err) {
      toast.error('Error like post')
      // Revert the like state
      setIsLiked((prev) => !prev)
      if (isLiked) {
        setTotalLiked((prev) => prev + 1)
      } else {
        setTotalLiked((prev) => (prev ? prev - 1 : 0))
      }
    }
  }

  if (!data) return null
  return (
    <div className={styles.react__card__ss}>
      <div
        className={styles.heat__react__card__ss}
        onClick={() => {
          handleChangePutFeedLike(data.postId)
        }}
      >
        <Image src={isLiked ? ICONS.heart : ICONS.heat} alt="sightsea" />
        <p
          style={{
            color: '#808191',
          }}
        >
          {formatNumberUser(totalLiked)}
        </p>
      </div>
      <div onClick={onComment} className={`${styles.comment__react__card__ss} cursor-pointer`}>
        <Image src={ICONS.comment} alt="sightsea" />
        <p className="text-ss-grayscale-500">{`${data.commentCount}`}</p>
      </div>

      {/* <div
        style={{
          background:
            votes[index] === 'unvote'
              ? ''
              : votes[index] || data?.voteType === 'upvote' || data?.voteType === 'downvote'
                ? '#6C5DD3'
                : '',
        }}
        className={styles.upDown__vote__card__ss}
      >
        <div
          className={styles.up__vote}
          onClick={() => handleUpvote(index, data.postId, data.upvoteCount, data.voteType)}
        >
          <Image
            className={
              votes[index] === 'unvote'
                ? ''
                : votes[index] || data?.voteType === 'upvote' || data?.voteType === 'downvote'
                  ? styles.whiteIcon
                  : ''
            }
            src={ICONS.up}
            alt="sightsea"
          />
          <span
            style={{
              color:
                votes[index] === 'unvote'
                  ? '#808191'
                  : votes[index] || data?.voteType === 'upvote' || data?.voteType === 'downvote'
                    ? '#E4E4E4'
                    : '#808191',
            }}
          >{`${data.upvoteCount + (totals[index] || 0)}`}</span>
        </div>
        <div className="h-[16px] w-[1px] bg-[#E4E4E4]"></div>
        <div
          className={styles.down__vote}
          onClick={() => handleDownvote(index, data.postId, data.downvoteCount, data.voteType)}
        >
          <Image
            className={
              votes[index] === 'unvote'
                ? ''
                : votes[index] || data?.voteType === 'upvote' || data?.voteType === 'downvote'
                  ? styles.whiteIcon
                  : ''
            }
            src={ICONS.down}
            alt="sightsea"
          />
        </div>
      </div> */}

      {/* share post */}

      <div onClick={onSharePost} className="flex h-[22px] w-[22px] cursor-pointer items-center justify-center">
        <Image width={20} height={20} src={ICONS.share} alt="" />
      </div>

      {/* <ShareFeed id={data.postId} /> */}
    </div>
  )
}

ItemFeed.ItemFeedContent = ItemFeedContent
ItemFeed.FeedName = FeedName
ItemFeed.FeedContent = FeedContent
ItemFeed.FeedImages = FeedImages
ItemFeed.FeedMetadata = FeedMetadata
ItemFeed.FeedAction = FeedAction
ItemFeed.FeedShare = FeedShare

export { ItemFeed }
